import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  Box, 
  AppBar, 
  Toolbar, 
  Container, 
  Card, 
  CardContent, 
  Avatar, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Notifications, 
  AccountCircle,
  Dashboard as DashboardIcon, 
  Call as CallIcon, 
  BarChart as AnalyticsIcon,
  ExitToApp as LogoutIcon,
  PhoneForwarded as OutboundIcon,
  People as PeopleIcon,
  Science as TestIcon,
  Feedback as FeedbackIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  BatchPrediction as BatchesIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  PlayArrow as PlayIcon,
  Article as SummaryIcon,
  TextFields as TranscriptIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  Storage as DataIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle,
  Cancel,
  HelpOutline,
  Schedule as ScheduledIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService, batchesService, organizationService, callsService } from '../utils/api';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';
import FeedbackList from './FeedbackList';
import { toast } from 'react-toastify';

function Batches() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [batchDetailsModalOpen, setBatchDetailsModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchDetailsLoading, setBatchDetailsLoading] = useState(false);
  const [batchDetailsError, setBatchDetailsError] = useState(null);
  const [downloadFailedLoading, setDownloadFailedLoading] = useState(false);
  const [downloadInterestedLoading, setDownloadInterestedLoading] = useState(false);
  
  // Call details modal state
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [callDetailsModalOpen, setCallDetailsModalOpen] = useState(false);

  // Refresh state
  const [refreshLoading, setRefreshLoading] = useState(false);

  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);
  
  // Get user role for conditional rendering
  const userRole = authService.getUserRole();
  const canViewCosts = userRole === 'super_admin' || userRole === 'admin';
  const canAccessOutbound = userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator';

  // Load current organization on component mount
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
  }, []);

  // Fetch batches (campaigns)
  const fetchBatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await batchesService.getBatches();
      setCampaigns(data.results || []);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError(err.message || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
    // Automatically refresh calls in the background when page loads
    refreshCallsInBackground();
  }, []);

  // Automatically refresh calls in the background
  const refreshCallsInBackground = async () => {
    try {
      await callsService.refreshCalls();
      console.log('Calls refreshed successfully in background');
    } catch (err) {
      console.error('Background call refresh failed:', err);
      // Don't show error toast as this is a background operation
    }
  };

  // Fetch batch details
  const fetchBatchDetails = async (batchId) => {
    try {
      setBatchDetailsLoading(true);
      setBatchDetailsError(null);
      const [data, callDetailsData] = await Promise.all([
        batchesService.getBatchById(batchId),
        batchesService.getBatchCallDetails(batchId),
      ]);

      const callDetailsByTaskId = {};
      (callDetailsData?.tasks || []).forEach((t) => {
        callDetailsByTaskId[t.id] = t.call_details;
      });

      const tasks = (data.tasks || []).map((t) => ({
        ...t,
        call_details: callDetailsByTaskId[t.id] || t.call_details,
      }));

      setBatchDetails({ ...data, tasks });
    } catch (err) {
      console.error('Error fetching batch details:', err);
      setBatchDetailsError(err.message || 'Failed to fetch batch details');
    } finally {
      setBatchDetailsLoading(false);
    }
  };

  // Handle view batch details
  const handleViewBatchDetails = async (batchId) => {
    setSelectedBatchId(batchId);
    setBatchDetailsModalOpen(true);
    await fetchBatchDetails(batchId);
  };

  // Handle close batch details modal
  const handleCloseBatchDetailsModal = () => {
    setBatchDetailsModalOpen(false);
    setSelectedBatchId(null);
    setBatchDetails(null);
    setBatchDetailsError(null);
  };

  const handleDownloadFailedCalls = async () => {
    if (!selectedBatchId) return;
    try {
      setDownloadFailedLoading(true);
      const response = await batchesService.downloadBatchFailedCalls(selectedBatchId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_${selectedBatchId}_failed_calls.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download failed calls:', err);
      toast.error(err?.message || 'Failed to download failed calls');
    } finally {
      setDownloadFailedLoading(false);
    }
  };

  const handleDownloadInterestedCalls = async () => {
    if (!selectedBatchId) return;
    try {
      setDownloadInterestedLoading(true);
      const response = await batchesService.downloadBatchInterestedCalls(selectedBatchId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_${selectedBatchId}_interested_calls.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download interested calls:', err);
      toast.error(err?.message || 'Failed to download interested calls');
    } finally {
      setDownloadInterestedLoading(false);
    }
  };

  // Get all unique variable keys from calls
  const getVariableKeys = () => {
    if (!batchDetails || !batchDetails.calls) return [];
    const allKeys = new Set();
    batchDetails.calls.forEach(call => {
      if (call.variable_values) {
        Object.keys(call.variable_values).forEach(key => allKeys.add(key));
      }
    });
    return Array.from(allKeys).sort();
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'info';
      case 'in-progress':
        return 'warning';
      case 'ended':
        return 'success';
      default:
        return 'default';
    }
  };

  const currencySymbol = process.env.REACT_APP_CURRENCY_SYMBOL || '';

  // Format duration helper
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    const totalMinutes = Math.floor(minutes);
    const seconds = Math.round((minutes - totalMinutes) * 60);
    if (totalMinutes === 0) {
      return `${seconds}s`;
    }
    return `${totalMinutes}m ${seconds}s`;
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined || isNaN(Number(cost))) return 'N/A';
    return `${currencySymbol}${Number(cost).toFixed(2)}`;
  };

  // Helper functions for call status display (from CallLogs)
  // Status helpers aligned with CallLogs; treat "completed" like "ended"
  const normalizeStatus = (status) => (status === 'completed' ? 'ended' : status);

  const getCallStatusColor = (status, endedReason) => {
    const st = normalizeStatus(status);
    if (st === 'ended') {
      if (endedReason === 'customer-ended-call' || endedReason === 'assistant-ended-call') {
        return 'success.main';
      }
      return 'error.main';
    }
    if (st === 'scheduled') {
      return '#f3a82b';
    }
    return 'text.secondary';
  };

  const getCallStatusIcon = (status, endedReason) => {
    const st = normalizeStatus(status);
    if (st === 'ended') {
      if (endedReason === 'customer-ended-call' || endedReason === 'assistant-ended-call') {
        return <CheckCircle fontSize="small" />;
      }
      return <Cancel fontSize="small" />;
    }
    if (st === 'scheduled') return <ScheduledIcon fontSize="small" />;
    return <HelpOutline fontSize="small" />;
  };

  const getCallStatusText = (status, endedReason) => {
    const st = normalizeStatus(status);
    if (st === 'ended') {
      if (endedReason === 'customer-ended-call') return 'Successful';
      if (endedReason === 'assistant-ended-call') return 'Successful';
      if (endedReason === 'exceeded-max-duration') return 'Successful';
      if (endedReason === 'customer-did-not-answer') return 'No Answer';
      if (endedReason === 'customer-busy') return 'Busy';
      return 'Failed';
    }
    if (st === 'scheduled') {
      return 'Scheduled';
    }
    return st || 'Unknown';
  };

  const getSuccessEvaluationColor = (successEvaluation) => {
    if (successEvaluation === 'true' || successEvaluation === true) {
      return 'success.main';
    }
    if (successEvaluation === 'false' || successEvaluation === false) {
      return 'error.main';
    }
    return 'text.secondary';
  };

  const getSuccessEvaluationText = (successEvaluation) => {
    if (successEvaluation === 'true' || successEvaluation === true) {
      return 'Interested';
    }
    if (successEvaluation === 'false' || successEvaluation === false) {
      return 'Not Interested';
    }
    return 'N/A';
  };

  const getSuccessEvaluationIcon = (successEvaluation) => {
    if (successEvaluation === 'true' || successEvaluation === true) {
      return <CheckCircle fontSize="small" />;
    }
    if (successEvaluation === 'false' || successEvaluation === false) {
      return <Cancel fontSize="small" />;
    }
    return <HelpOutline fontSize="small" />;
  };

  // Format field name helper
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Fetch call details
  const fetchCallDetails = async (callId, organizationId = null) => {
    try {
      setCallDetailsLoading(true);
      const detailsData = await callsService.getCallDetails(callId, organizationId);
      if (detailsData.result && detailsData.result.length > 0) {
        setCallDetails(detailsData.result[0]);
      } else {
        setCallDetails(null);
      }
    } catch (err) {
      console.error('Error fetching call details:', err);
      setCallDetails(null);
    } finally {
      setCallDetailsLoading(false);
    }
  };

  // Handle call ID click
  const handleCallClick = async (call) => {
    const callId = typeof call === 'string' ? call : call?.call_id;
    if (!callId) {
      toast.error('Call ID not available for this task.');
      return;
    }
    const orgId = currentOrganization?.id;
    setSelectedCallId(callId);
    setCallDetailsModalOpen(true);
    if (call?.call_details) {
      setCallDetails(call.call_details);
      setCallDetailsLoading(false);
      return;
    }
    await fetchCallDetails(callId, orgId);
  };

  // Handle call details modal close
  const handleCallDetailsModalClose = () => {
    setCallDetailsModalOpen(false);
    setTimeout(() => {
      setSelectedCallId(null);
      setCallDetails(null);
    }, 200);
  };

  // Handle copy link
  const handleCopyLink = (callId) => {
    const orgId = currentOrganization?.id;
    const url = `${window.location.origin}/call-logs/${callId}${orgId ? `?org=${orgId}` : ''}`;
    navigator.clipboard.writeText(url);
    // Could add toast notification here
  };

  const handleRefresh = async () => {
    try {
      setRefreshLoading(true);
      // Same refresh API as Call Logs page
      await callsService.refreshCalls();
      // Re-fetch batches after refresh
      await fetchBatches();
    } catch (err) {
      console.error('Error refreshing batches:', err);
      setError(err.message || 'Failed to refresh batches');
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    const confirmDelete = window.confirm('Are you sure you want to cancel this batch?');
    if (!confirmDelete) return;

    try {
      setError(null);
      await batchesService.cancelBatch(batchId);
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === batchId ? { ...c, status: 'cancelled' } : c
        )
      );
      toast.success('Call batch cancelled successfully.');
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError(err.message || 'Failed to delete batch');
      toast.error(err.message || 'Failed to cancel call batch.');
    }
  };

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...(canAccessOutbound && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = window.location.pathname === item.path;
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(239, 71, 111, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
            },
            '&:active': {
              transform: 'translateX(1px)'
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: 40,
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
              '.MuiListItem-root:hover &': {
                color: 'error.main'
              }
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out'
            }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8f9fc' }}>
      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', ml: { md: 0 } }}>
        {/* AppBar */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            bgcolor: '#ffffff',
            borderBottom: '1px solid rgba(102, 117, 140, 0.12)',
            color: 'text.primary'
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Batches
            </Typography>

            {/* Organization Switcher Button (same style as other pages) */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}

            <IconButton color="inherit">
              <Badge badgeContent={0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            <IconButton
              onClick={handleOpenMenu}
              size="small"
              sx={{ ml: 2 }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Main Content Area */}
        <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Call Batches
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={refreshLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={refreshLoading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {refreshLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>

              {error && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                </Box>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : campaigns.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No batches found
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Batch Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>ETA End</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaigns.map((campaign) => (
                        <TableRow 
                          key={campaign.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: 'action.hover' 
                            } 
                          }}
                        >
                          <TableCell>{campaign.name || 'N/A'}</TableCell>
                          <TableCell>{formatDate(campaign.start_time || campaign.start_from)}</TableCell>
                          <TableCell>{formatDate(campaign.eta_time || campaign.end_till)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={campaign.status || 'unknown'} 
                              color={getStatusColor(campaign.status)}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleViewBatchDetails(campaign.id)}
                              sx={{ color: 'primary.main', mr: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            {campaign.status !== 'ended' && campaign.status !== 'cancelled' && campaign.status !== 'completed' && campaign.status !== 'partially_completed' && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBatch(campaign.id)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Batch Details Modal */}
              <Dialog
                open={batchDetailsModalOpen}
                onClose={handleCloseBatchDetailsModal}
                maxWidth="lg"
                fullWidth
              >
                <DialogTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BatchesIcon />
                    <Typography variant="h6">Batch Details</Typography>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  {batchDetailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : batchDetailsError ? (
                    <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="error">
                        {batchDetailsError}
                      </Typography>
                    </Box>
                  ) : batchDetails && batchDetails.tasks ? (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: 'success.dark',
                            bgcolor: 'success.50',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            display: 'inline-block'
                          }}
                        >
                          Total Calls: {batchDetails.tasks.length}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="medium"
                            variant="contained"
                            color="primary"
                            startIcon={downloadInterestedLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                            onClick={handleDownloadInterestedCalls}
                            disabled={downloadInterestedLoading}
                            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 200 }}
                          >
                            {downloadInterestedLoading ? 'Preparing CSV...' : 'Download Interested Calls'}
                          </Button>
                          <Button
                            size="medium"
                            variant="contained"
                            color="primary"
                            startIcon={downloadFailedLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                            onClick={handleDownloadFailedCalls}
                            disabled={downloadFailedLoading}
                            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 200 }}
                          >
                            {downloadFailedLoading ? 'Preparing CSV...' : 'Download Failed Calls'}
                          </Button>
                        </Box>
                      </Box>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 950 }}>
                          <Box sx={{ display: 'flex', borderBottom: 2, borderColor: 'divider', pb: 2, px: 1 }}>
                            <Typography variant="subtitle1" sx={{ flex: '1 1 18%', color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                              PHONE NUMBER
                            </Typography>
                            <Typography variant="subtitle1" sx={{ flex: '1 1 14%', color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                              STATUS
                            </Typography>
                            <Box sx={{ flex: '1 1 14%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem', lineHeight: 1.1 }}>
                                SUCCESS
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem', lineHeight: 1.1 }}>
                                EVALUATION
                              </Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ flex: '1 1 12%', color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                              DURATION
                            </Typography>
                            <Typography variant="subtitle1" sx={{ flex: '1 1 10%', color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                              COST
                            </Typography>
                            <Typography variant="subtitle1" sx={{ flex: '1 1 20%', color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                              ENDED REASON
                            </Typography>
                            <Typography variant="subtitle1" sx={{ flex: '1 1 15%', color: 'text.primary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                              ACTIONS
                            </Typography>
                          </Box>

                          {batchDetails.tasks.map((call) => (
                            <Box
                              key={call.id}
                              sx={{
                                display: 'flex',
                                py: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                alignItems: 'center',
                                px: 1,
                                '&:hover': {
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  mx: -1,
                                  px: 2
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ flex: '1 1 20%', fontSize: '0.9rem', fontWeight: 500 }}>
                                {call.phone_number || 'N/A'}
                              </Typography>
                              <Box sx={{ flex: '1 1 15%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getCallStatusColor(call.status, call.ended_reason) }}>
                                  {getCallStatusIcon(call.status, call.ended_reason)}
                                  <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'inherit' }}>
                                    {getCallStatusText(call.status, call.ended_reason)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ flex: '1 1 15%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getSuccessEvaluationColor(call.success_evaluation) }}>
                                  {getSuccessEvaluationIcon(call.success_evaluation)}
                                  <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'inherit' }}>
                                    {getSuccessEvaluationText(call.success_evaluation)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography variant="body2" sx={{ flex: '1 1 15%', fontSize: '0.9rem', color: 'text.secondary' }}>
                                {formatDuration(call.minutes)}
                              </Typography>
                              <Typography variant="body2" sx={{ flex: '1 1 10%', fontSize: '0.9rem', color: 'text.secondary' }}>
                                {formatCost(call.cost)}
                              </Typography>
                              <Typography variant="body2" sx={{ flex: '1 1 20%', fontSize: '0.9rem', color: 'text.secondary' }}>
                                {call.ended_reason ? call.ended_reason.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                              </Typography>
                              <Box sx={{ flex: '1 1 15%' }}>
                                <Tooltip title={call.call_id ? "View Details" : "Call ID not available"} placement="top">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCallClick(call)}
                                      disabled={!call.call_id}
                                      sx={{ 
                                        color: 'primary.main',
                                        '&:hover': { 
                                          backgroundColor: 'primary.50',
                                          color: 'primary.dark'
                                        }
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No call details available
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseBatchDetailsModal} color="primary">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Call Details Modal */}
              <Dialog
                open={callDetailsModalOpen}
                onClose={handleCallDetailsModalClose}
                maxWidth="lg"
                fullWidth
              >
                <DialogTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <PhoneIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Call Details
                      </Typography>
                      {selectedCallId && (
                        <Typography variant="body2" color="text.secondary">
                          ID: {selectedCallId}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </DialogTitle>
                
                <DialogContent sx={{ p: 0 }}>
                  {callDetailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : callDetails ? (
                    <Box>
                      {/* Quick Stats Section */}
                      <Box sx={{ bgcolor: 'grey.50', p: 3 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                              <PhoneIcon sx={{ color: 'primary.main', fontSize: 28, mb: 1 }} />
                              <Typography variant="caption" color="text.secondary" display="block">
                                Phone Number
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {callDetails.phoneNumber}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                              <TimeIcon sx={{ color: 'info.main', fontSize: 28, mb: 1 }} />
                              <Typography variant="caption" color="text.secondary" display="block">
                                Duration
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {formatDuration(callDetails.duration)}
                              </Typography>
                            </Card>
                          </Grid>
                          {canViewCosts && (
                      <Grid item xs={12} sm={3}>
                              <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                                <MoneyIcon sx={{ color: 'success.main', fontSize: 28, mb: 1 }} />
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Total Cost
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatCost(callDetails.totalCost)}
                                </Typography>
                              </Card>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                              <PlayIcon sx={{ color: 'warning.main', fontSize: 28, mb: 1 }} />
                              <Typography variant="caption" color="text.secondary" display="block">
                                Recording
                              </Typography>
                              {callDetails.recordingUrl ? (
                                <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 500 }}>
                                  Available
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Not Available
                                </Typography>
                              )}
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Content Sections */}
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          {/* Audio Recording Section */}
                          {callDetails.recordingUrl && (
                            <Grid item xs={12}>
                              <Card variant="outlined" sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                    <PlayIcon />
                                  </Avatar>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Call Recording
                                  </Typography>
                                </Box>
                                <audio 
                                  controls 
                                  style={{ width: '100%' }}
                                  preload="metadata"
                                >
                                  <source src={callDetails.recordingUrl} type="audio/wav" />
                                  Your browser does not support the audio element.
                                </audio>
                              </Card>
                            </Grid>
                          )}

                          {/* Call Summary Section */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                  <SummaryIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Call Summary
                                </Typography>
                              </Box>
                              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                {callDetails.summary || 'No summary available for this call.'}
                              </Typography>
                            </Card>
                          </Grid>

                          {/* Structured Data Section */}
                          {callDetails.structuredData && (
                            <Grid item xs={12}>
                              <Card variant="outlined" sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                    <DataIcon />
                                  </Avatar>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Structured Data
                                  </Typography>
                                </Box>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table sx={{ minWidth: 650 }}>
                                    <TableBody>
                                      {Object.entries(callDetails.structuredData).map(([key, value]) => (
                                        <TableRow key={key} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                          <TableCell 
                                            component="th" 
                                            scope="row" 
                                            sx={{ 
                                              fontWeight: 600,
                                              minWidth: 200,
                                              borderRight: 1,
                                              borderColor: 'divider'
                                            }}
                                          >
                                            {formatFieldName(key)}
                                          </TableCell>
                                          <TableCell sx={{ wordBreak: 'break-word' }}>
                                            {value || 'N/A'}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Card>
                            </Grid>
                          )}

                          {/* Assistant Variables Section */}
                          {callDetails.assistantVariables && Object.keys(callDetails.assistantVariables).length > 0 && (
                            <Grid item xs={12}>
                              <Card variant="outlined" sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                                    <SettingsIcon />
                                  </Avatar>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Assistant Variables
                                  </Typography>
                                </Box>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table sx={{ minWidth: 650 }}>
                                    <TableBody>
                                      {Object.entries(callDetails.assistantVariables).map(([key, value]) => (
                                        <TableRow key={key} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                          <TableCell 
                                            component="th" 
                                            scope="row" 
                                            sx={{ 
                                              fontWeight: 600,
                                              minWidth: 200,
                                              borderRight: 1,
                                              borderColor: 'divider'
                                            }}
                                          >
                                            {formatFieldName(key)}
                                          </TableCell>
                                          <TableCell sx={{ wordBreak: 'break-word' }}>
                                            {value || 'N/A'}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Card>
                            </Grid>
                          )}

                          {/* Transcript Section */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                    <TranscriptIcon />
                                  </Avatar>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Call Transcript
                                  </Typography>
                                </Box>
                                {callDetails.transcript && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CopyIcon />}
                                    onClick={() => {
                                      navigator.clipboard.writeText(callDetails.transcript);
                                    }}
                                  >
                                    Copy
                                  </Button>
                                )}
                              </Box>
                              
                              {callDetails.transcript ? (
                                <Box sx={{ 
                                  maxHeight: 400, 
                                  overflow: 'auto', 
                                  bgcolor: 'grey.50', 
                                  p: 3, 
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'grey.200'
                                }}>
                                  {callDetails.transcript.split('\n').map((line, index) => {
                                    const isAI = line.startsWith('AI:');
                                    const isUser = line.startsWith('User:');
                                    
                                    return (
                                      <Box key={index} sx={{ mb: 2 }}>
                                        {isAI && (
                                          <Typography variant="caption" sx={{ 
                                            display: 'block',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            mb: 0.5
                                          }}>
                                            AI Assistant:
                                          </Typography>
                                        )}
                                        {isUser && (
                                          <Typography variant="caption" sx={{ 
                                            display: 'block',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            mb: 0.5
                                          }}>
                                            Customer:
                                          </Typography>
                                        )}
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            ml: isAI || isUser ? 1 : 0,
                                            lineHeight: 1.6,
                                            color: 'text.primary'
                                          }}
                                        >
                                          {isAI || isUser ? line.substring(line.indexOf(':') + 1).trim() : line}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Box sx={{ 
                                  textAlign: 'center', 
                                  py: 4, 
                                  bgcolor: 'grey.50', 
                                  borderRadius: 2 
                                }}>
                                  <TranscriptIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                                  <Typography variant="body1" color="text.secondary">
                                    No transcript available for this call
                                  </Typography>
                                </Box>
                              )}
                            </Card>
                          </Grid>

                          {/* Feedback Section */}
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 3 }}>
                              <FeedbackList callId={selectedCallId} />
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="h6" color="text.secondary">
                        No call details available
                      </Typography>
                    </Box>
                  )}
                </DialogContent>
                
                <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
                  {selectedCallId && (
                    <Button 
                      onClick={() => handleCopyLink(selectedCallId)}
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      sx={{ mr: 'auto' }}
                    >
                      Copy Link
                    </Button>
                  )}
                  <Button 
                    onClick={handleCallDetailsModalClose} 
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 100 }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}

export default Batches;
